import i18n from "i18next";
import { createBatchedAnalyticsSetter } from "@bto/shared";
import { BASE_URL } from "../utils/requestUtils";
import { getSanitizedUserData } from "../utils/contextUtils";
import { UserAuthClientResponse } from "../recoil/types";

export const BatchedSetAnalyticsUser = createBatchedAnalyticsSetter<
  [UserAuthClientResponse | undefined, string | undefined]
>((patientInfo, distinct_id, properties) => {
  SetAnalyticsUser(patientInfo, distinct_id, properties);
});

export const TrackAnalyticsEvent = async (
  event_name: string,
  token?: string,
  distinct_id?: string,
  properties?: { [key: string]: string | number },
): Promise<Response | undefined> => {
  if (!token && !distinct_id) {
    console.error(
      `Either token or distinct_id must be provided for event '${event_name}'`,
    );
    return;
  }

  const payload: {
    properties?: { [key: string]: string | number };
    distinct_id?: string;
    event_name: string;
  } = { event_name };

  const locale = i18n.language || navigator.language || "en";

  payload.properties = { locale, ...(properties || {}) };

  if (distinct_id) {
    payload.distinct_id = distinct_id;
  }

  let headers: { "Content-Type": string; Authorization?: string } = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers = { ...headers, Authorization: `Bearer ${token}` };
  }

  try {
    const response = await fetch(`${BASE_URL}/api/v1/analytics/events/`, {
      body: JSON.stringify(payload),
      method: "POST",
      headers,
    });
    return response;
  } catch (e) {
    console.error(`Error sending analytics event: '${e}'`);
    return undefined;
  }
};

export const SetAnalyticsUser = async (
  patientInfo?: UserAuthClientResponse,
  distinct_id?: string,
  properties?: { [key: string]: string | number },
): Promise<Response | undefined> => {
  if (!patientInfo && !distinct_id) {
    console.error("Either auth info or distinct_id must be provided");
    return;
  }

  const payload: {
    properties?: { [key: string]: string | number };
    distinct_id?: string;
  } = {};

  if (properties) {
    payload.properties = {
      ...payload.properties,
      ...properties,
    };
  }

  if (patientInfo) {
    const userContext = getSanitizedUserData(patientInfo);
    payload.properties = {
      ...payload.properties,
      ...userContext,
    };
  }

  if (distinct_id) {
    payload.distinct_id = distinct_id;
  }

  let headers: { "Content-Type": string; Authorization?: string } = {
    "Content-Type": "application/json",
  };

  if (patientInfo?.auth0.access_token) {
    headers = {
      ...headers,
      Authorization: `Bearer ${patientInfo.auth0.access_token}`,
    };
  }

  try {
    const response = await fetch(`${BASE_URL}/api/v1/analytics/users/`, {
      body: JSON.stringify(payload),
      method: "POST",
      headers,
    });
    return response;
  } catch (e) {
    console.error(`Error setting analytics user: '${e}'`);
    return undefined;
  }
};
